// -----------------------------------------------------------------------------
// ui/preview/stage.ts — the preview canvas + transport bar.
//
// Hosts the compositor's canvas (the engine draws into it on its own clock) and a
// transport bar beneath it: jump-to-start, play/pause, and a time / duration
// readout. The UI only sends play/pause/seek to the player and reflects its
// events — it never draws frames itself. (CLAUDE.md §4 principle 5.)
// -----------------------------------------------------------------------------
import { UpdateProjectCommand } from '../../core/commands/project-commands';
import { projectDuration } from '../../core/model';
import { formatTimecode } from '../../shared/time';
import type { EditorContext } from '../context';
import { el } from '../dom';

// Canvas aspect-ratio presets (label → pixel dimensions at a 1080-class size).
const SIZE_PRESETS = [
  { label: 'Wide 16:9', w: 1920, h: 1080 },
  { label: 'Vertical 9:16', w: 1080, h: 1920 },
  { label: 'Square 1:1', w: 1080, h: 1080 },
  { label: 'Classic 4:3', w: 1440, h: 1080 },
  { label: 'Social 4:5', w: 1080, h: 1350 },
  { label: 'Cinema 21:9', w: 2560, h: 1080 },
  { label: 'Portrait 2:3', w: 1080, h: 1620 },
];

export function createStage(ctx: EditorContext, previewCanvas: HTMLCanvasElement): HTMLElement {
  const { player, store } = ctx;

  // Onboarding hint, shown over the (empty) preview until there's something to play.
  const hint = el(
    'div',
    { class: 'zf-stage-hint' },
    el('div', { class: 'zf-stage-hint-title' }, 'Nothing on the timeline yet'),
    el('div', {}, 'Import a video or image, or add a text overlay to begin.'),
  );
  const stage = el('div', { class: 'zf-stage' }, previewCanvas, hint);

  const refreshHint = (): void => {
    hint.style.display = projectDuration(store.state.project) > 0 ? 'none' : '';
  };
  store.subscribe(refreshHint);
  refreshHint();

  // Fit the preview canvas inside the stage (letterbox/contain). CSS percentage
  // max-height doesn't reliably constrain a canvas in a grid cell, so we size it
  // explicitly — recomputed on resize and whenever the project aspect changes.
  const fitPreview = (): void => {
    const availW = stage.clientWidth - 32;
    const availH = stage.clientHeight - 32;
    if (availW <= 0 || availH <= 0) return;
    const aspect = previewCanvas.width / previewCanvas.height || 16 / 9;
    let w = availW;
    let h = w / aspect;
    if (h > availH) {
      h = availH;
      w = h * aspect;
    }
    previewCanvas.style.width = `${Math.round(w)}px`;
    previewCanvas.style.height = `${Math.round(h)}px`;
  };
  new ResizeObserver(fitPreview).observe(stage);
  store.subscribe(fitPreview); // aspect can change (Size presets / project load)
  requestAnimationFrame(fitPreview);

  const toStart = el(
    'button',
    { class: 'zf-btn zf-btn-ghost', title: 'Jump to start', 'aria-label': 'Jump to start', onclick: () => player.seek(0) },
    '⏮',
  );
  const playBtn = el(
    'button',
    { class: 'zf-btn zf-btn-primary zf-play', title: 'Play / pause (Space)', 'aria-label': 'Play or pause', onclick: () => player.toggle() },
    '▶',
  );
  const timeEl = el('span', { class: 'zf-time' }, '0:00.00 / 0:00.00');

  const dimsEl = el('span', { class: 'zf-time' });
  const transport = el(
    'div',
    { class: 'zf-transport' },
    toStart,
    playBtn,
    timeEl,
    el('span', { class: 'zf-transport-spacer' }),
    dimsEl,
  );

  const updateTime = (): void => {
    timeEl.textContent = `${formatTimecode(player.getTime())} / ${formatTimecode(projectDuration(store.state.project))}`;
    const p = store.state.project;
    dimsEl.textContent = `${p.width}×${p.height} · ${p.fps}fps`;
  };
  player.events.on('time', updateTime);
  player.events.on('playing', (playing) => {
    playBtn.textContent = playing ? '❚❚' : '▶';
  });
  store.subscribe(updateTime);
  updateTime();

  // ---- Size + background toolbar ---------------------------------------------
  const sizeSelect = el('select', { class: 'zf-select', 'aria-label': 'Canvas size' }) as HTMLSelectElement;
  for (const preset of SIZE_PRESETS) {
    sizeSelect.append(el('option', { value: `${preset.w}x${preset.h}` }, preset.label));
  }
  sizeSelect.append(el('option', { value: 'custom' }, 'Custom'));
  sizeSelect.addEventListener('change', () => {
    const [w, h] = sizeSelect.value.split('x').map(Number);
    if (w > 0 && h > 0) {
      store.dispatch(new UpdateProjectCommand({ width: w, height: h }));
      ctx.setStatus(`Canvas set to ${w}×${h}.`);
    }
  });

  const bgInput = el('input', {
    type: 'color',
    class: 'zf-bg-color',
    'aria-label': 'Background colour',
    title: 'Background colour',
  }) as HTMLInputElement;
  let origBg: string | undefined;
  bgInput.addEventListener('input', () => {
    if (origBg === undefined) origBg = store.state.project.background;
    store.state.project.background = bgInput.value; // live preview
    store.notify();
  });
  bgInput.addEventListener('change', () => {
    const value = bgInput.value;
    if (origBg === undefined) origBg = value;
    store.state.project.background = origBg; // restore, then commit one undoable step
    store.dispatch(new UpdateProjectCommand({ background: value }));
    origBg = undefined;
  });

  const toolbar = el(
    'div',
    { class: 'zf-stage-toolbar' },
    el('span', { class: 'zf-toolbar-label' }, 'Size'),
    sizeSelect,
    el('span', { class: 'zf-toolbar-label' }, 'Background'),
    bgInput,
  );
  const refreshToolbar = (): void => {
    const p = store.state.project;
    const val = `${p.width}x${p.height}`;
    sizeSelect.value = SIZE_PRESETS.some((pr) => `${pr.w}x${pr.h}` === val) ? val : 'custom';
    if (/^#[0-9a-f]{6}$/i.test(p.background)) bgInput.value = p.background;
  };
  store.subscribe(refreshToolbar);
  refreshToolbar();

  return el('div', { class: 'zf-stage-wrap' }, toolbar, stage, transport);
}
